import React, { useEffect } from 'react';
import { ShieldCheck, SlidersHorizontal, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import './CookieConsent.css';

const CookieConsent = () => {
  const {
    consent,
    hasDecision,
    settingsOpen,
    closeCookieSettings,
    dismissCookieReminder,
    restoreCookieReminder,
    reminderDismissed,
    acceptCookies,
    rejectNonEssential,
  } = useCookieConsent();
  const { pathname } = useLocation();

  // "Not now" only silences the reminder on the page the visitor is reading.
  // Every navigation brings it back, so someone who turned optional cookies off
  // keeps being offered the chance to turn them on again until they actually
  // press "Enable cookies" - at which point the reminder stops for good, since
  // it only renders while the stored choice is 'rejected'.
  useEffect(() => {
    restoreCookieReminder();
  }, [pathname, restoreCookieReminder]);

  const rejectedReminder = consent?.choice === 'rejected' && !settingsOpen && !reminderDismissed;
  const hiddenAccepted = consent?.choice === 'accepted' && !settingsOpen;
  const hiddenRejected = consent?.choice === 'rejected' && !settingsOpen && reminderDismissed;
  if (hiddenAccepted || hiddenRejected) return null;

  if (rejectedReminder) {
    return (
      <div className="cookie-consent cookie-consent--reminder" role="dialog" aria-labelledby="cookie-consent-reminder-title">
        <div className="cookie-consent__content">
          <div className="cookie-consent__title-row">
            <h2 id="cookie-consent-reminder-title">Improve your Eulua experience</h2>
            <button type="button" className="cookie-consent__close" onClick={dismissCookieReminder} aria-label="Dismiss cookie recommendation"><X size={18} /></button>
          </div>
          <p>
            Optional cookies are currently off. Enable them to receive more relevant product recommendations and sponsored content based on the products you view, and to let us measure aggregate site usage with analytics.
          </p>
          <Link to="/cookies#controls">Review your cookie choices</Link>
        </div>
        <div className="cookie-consent__actions">
          <button type="button" className="cookie-consent__reject" onClick={dismissCookieReminder}>Not now</button>
          <button type="button" className="cookie-consent__accept" onClick={acceptCookies}>Enable cookies</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cookie-consent" role="dialog" aria-modal="true" aria-labelledby="cookie-consent-title">
      <div className="cookie-consent__content">
        <div className="cookie-consent__title-row">
          <h2 id="cookie-consent-title">Your privacy choices</h2>
          {hasDecision && <button type="button" className="cookie-consent__close" onClick={closeCookieSettings} aria-label="Close cookie settings"><X size={18} /></button>}
        </div>
        <p>
          Eulua uses connection information to estimate an approximate location and necessary browser storage for sign-in, security, your cart, delivery location, language, and active currency. With your permission, we also remember viewed products, use marketplace activity to personalize recommendations and sponsored content, and use Google Analytics to measure aggregate site usage.
        </p>
        <div className="cookie-consent__labels" aria-label="Cookie categories">
          <span><ShieldCheck size={14} /> Necessary · Always active</span>
          <span><SlidersHorizontal size={14} /> Recommendations, personalization, and analytics · Optional</span>
        </div>
        <Link to="/cookies">Read our Cookie Policy</Link>
      </div>
      <div className="cookie-consent__actions">
        <button type="button" className="cookie-consent__reject" onClick={rejectNonEssential}>Reject non-essential</button>
        <button type="button" className="cookie-consent__accept" onClick={acceptCookies}>Accept cookies</button>
      </div>
    </div>
  );
};

export default CookieConsent;
